import { useId, type ReactNode } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ContactSelector } from "@/components/quotes/ContactSelector";
import { useToggleLost, type QuoteRow } from "@/hooks/useQuotes";
import { useEventTypes } from "@/hooks/usePackages";
import { ghlContactHref } from "@/lib/ghl-links";
import { SERVICE_TYPES, SERVICE_TYPE_SHORT_LABELS, effectiveServiceType, formatEventStartTime, isPickup, type ServiceType } from "@/types/quotes";
import { cameFrom, canEditQuote, lastContacted } from "./drawerLogic";
import type { EventDetailsDraft } from "./useEventDetailsDraft";

interface EventClientSectionProps {
  quote: QuoteRow;
  details: EventDetailsDraft;
  /** Edit details is open; lifted so the drawer can close it after a discard. */
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  /** The quote draft has unsaved item edits that Save details would save too. */
  quoteDirty: boolean;
}

const PICKUP_AT = "At Tristano's, Bolton";
const fieldCls = "grid gap-[3px] text-[12px] text-muted-foreground";
const selectCls = "flex h-8 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground disabled:opacity-50";
const ghost = (text: string) => <span className="text-muted-foreground">{text}</span>;

function longDate(value: string | null): string {
  const d = value ? parseISO(value) : null;
  return d && !Number.isNaN(d.getTime()) ? format(d, "EEE, MMM d") : "";
}

async function copy(value: string) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`Copied ${value}`);
  } catch {
    toast.error(`Select and copy: ${value}`);
  }
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid min-w-0 gap-px">
      <span className="text-[11px] font-bold uppercase tracking-[.08em] text-muted-foreground">{label}</span>
      <span className="text-[13.5px] [overflow-wrap:anywhere]">{children}</span>
    </div>
  );
}

function CopyFact({ label, value }: { label: string; value: string | null }) {
  if (!value) return <Fact label={label}>{ghost("Not set")}</Fact>;
  return (
    <Fact label={label}>
      <span className="flex items-center gap-1.5">
        <span className="min-w-0 [overflow-wrap:anywhere]">{value}</span>
        <button type="button" aria-label={`Copy ${label.toLowerCase()}`} onClick={() => copy(value)}
          className="flex-none rounded-[5px] border bg-background px-1.5 text-[11.5px] font-semibold text-muted-foreground hover:text-foreground">
          Copy
        </button>
      </span>
    </Fact>
  );
}

function Facts({ quote }: { quote: QuoteRow }) {
  const svc = effectiveServiceType(quote.service_type, quote.fulfillment_type) as ServiceType;
  const pickup = isPickup(quote.service_type, quote.fulfillment_type);
  return (
    <div className="grid grid-cols-3 gap-x-4 gap-y-2.5 tabular-nums max-[760px]:grid-cols-2">
      <Fact label="Date">{longDate(quote.event_date) || ghost("Not set")}</Fact>
      <Fact label="Time">{formatEventStartTime(quote.event_start_time) || ghost("Not set")}</Fact>
      <Fact label="Guests">{Number(quote.guest_count) || 0}</Fact>
      <Fact label="Service">{SERVICE_TYPE_SHORT_LABELS[svc] ?? quote.service_type}</Fact>
      <Fact label="Event type">{quote.event_type?.name ?? ghost("Not set")}</Fact>
      <Fact label={pickup ? "Pickup" : "Address"}>{pickup ? PICKUP_AT : quote.event_address || ghost("Not set")}</Fact>
      <Fact label="Client">{quote.client_name || ghost("Not set")}</Fact>
      <CopyFact label="Phone" value={quote.client_phone} />
      <CopyFact label="Email" value={quote.client_email} />
      <Fact label="Came from">{cameFrom(quote)}</Fact>
      <Fact label="Last contacted">{lastContacted(quote.contact)}</Fact>
    </div>
  );
}

/** Edit details: the event's fields, this quote's service and event type, and the client (#1559). */
function DetailsForm({ quote, details, quoteDirty, onDone }: Omit<EventClientSectionProps, "editing" | "onEditingChange"> & { onDone: () => void }) {
  const id = useId();
  const eventTypes = useEventTypes().data ?? [];
  const { values, errors } = details;
  const locked = !canEditQuote(quote) ? (quote.square_source ? "Change it in Square" : "Locked on an accepted quote") : null;
  const pickup = isPickup(details.serviceType);
  const text = (key: "event_date" | "event_time" | "guest_count" | "delivery_address", label: string, type = "text") => (
    <div className={fieldCls}>
      <label htmlFor={`${id}-${key}`}>{label}</label>
      <Input id={`${id}-${key}`} type={type} value={values[key]} onChange={(e) => details.set(key, e.target.value)}
        inputMode={key === "guest_count" ? "numeric" : undefined} className="h-8 text-foreground" />
      {errors[key] && <span className="text-destructive">{errors[key]}</span>}
    </div>
  );
  return (
    <div className="grid gap-2.5">
      <div className="grid grid-cols-3 gap-2.5 max-[760px]:grid-cols-2">
        {text("event_date", "Date", "date")}
        {text("event_time", "Time", "time")}
        {text("guest_count", "Guests")}
        <div className={fieldCls}>
          <label htmlFor={`${id}-svc`}>Service</label>
          <select id={`${id}-svc`} value={details.serviceType} disabled={!!locked} onChange={(e) => details.setServiceType(e.target.value)} className={selectCls}>
            {SERVICE_TYPES.map((s) => <option key={s} value={s}>{SERVICE_TYPE_SHORT_LABELS[s]}</option>)}
          </select>
        </div>
        <div className={fieldCls}>
          <label htmlFor={`${id}-type`}>Event type</label>
          <select id={`${id}-type`} value={details.eventTypeId} disabled={!!locked} onChange={(e) => details.setEventTypeId(e.target.value)} className={selectCls}>
            <option value="">None</option>
            {eventTypes.filter((t) => t.is_active || t.id === details.eventTypeId).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        {pickup ? (
          <div className={fieldCls}>
            <label htmlFor={`${id}-pickup`}>Pickup</label>
            <Input id={`${id}-pickup`} disabled value={PICKUP_AT} className="h-8" />
          </div>
        ) : (
          text("delivery_address", "Address")
        )}
        {locked && <p className="col-span-full text-[12px] text-muted-foreground">Service and event type: {locked}</p>}
        <div className="col-span-2 grid gap-[3px] text-[12px] text-muted-foreground max-[760px]:col-span-full">
          Client
          <ContactSelector
            clientName={values.client_name}
            clientEmail={values.client_email}
            clientPhone={values.client_phone}
            contactId={details.contactId}
            onSelect={details.client.select}
            onClear={details.client.clear}
            onManualChange={details.client.manualChange}
          />
          {(errors.client_name || errors.client_email || errors.client_phone) && (
            <span className="text-destructive">{errors.client_name ?? errors.client_email ?? errors.client_phone}</span>
          )}
        </div>
        <p className="col-span-full text-[12.5px] text-muted-foreground">Changes here update the event and every quote on it.</p>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        {quoteDirty && details.typesChanged && !locked && (
          <span className="text-[12px] text-muted-foreground">Also saves your quote changes.</span>
        )}
        <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => { details.reset(); onDone(); }}>Cancel</Button>
        <Button type="button" size="sm" className="h-8" disabled={details.saving}
          onClick={async () => { if (await details.save()) onDone(); }}>
          Save details
        </Button>
      </div>
    </div>
  );
}

/** Event & client: the facts grid, Copy, Message in GHL, lost / re-open, and Edit details (#1559). */
export function EventClientSection({ quote, details, editing, onEditingChange, quoteDirty }: EventClientSectionProps) {
  const toggleLost = useToggleLost();
  const ghl = ghlContactHref(quote.contact?.ghl_location_id, quote.contact?.ghl_contact_id);
  return (
    <section data-section="event-client" className="rounded-[10px] border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3.5 py-2.5">
        <h3 className="font-display text-[15px] font-bold">Event &amp; client</h3>
        {editing ? (
          <span className="text-[12.5px] text-muted-foreground">Editing the event</span>
        ) : (
          <div className="flex flex-wrap gap-2">
            {ghl ? (
              <Button asChild variant="outline" size="sm" className="h-8">
                <a href={ghl} target="_blank" rel="noopener noreferrer">Message in GHL</a>
              </Button>
            ) : (
              <Button type="button" variant="outline" size="sm" className="h-8" disabled title="Not synced to GHL yet">Message in GHL</Button>
            )}
            {quote.event_id && (
              <Button type="button" variant="outline" size="sm" className="h-8" disabled={!details.eventReady} onClick={() => onEditingChange(true)}>
                Edit details
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="grid gap-2.5 px-3.5 py-3">
        {editing ? (
          <DetailsForm quote={quote} details={details} quoteDirty={quoteDirty} onDone={() => onEditingChange(false)} />
        ) : (
          <>
            <Facts quote={quote} />
            {quote.event_id && details.eventError && (
              <p className="text-[12.5px] text-destructive">Couldn't load the event, so its details can't be edited. Reload to try again.</p>
            )}
            <div className="flex flex-wrap items-center justify-between gap-1.5">
              <span className="text-[12.5px] text-muted-foreground">These belong to the event, so every quote on it shows the same.</span>
              <button
                type="button"
                disabled={toggleLost.isPending}
                onClick={() => toggleLost.mutate({ id: quote.id, is_lost: !quote.is_lost })}
                className={`text-[13px] font-bold ${quote.is_lost ? "text-accent" : "text-status-hot"}`}
              >
                {quote.is_lost ? "Re-open event" : "Mark event lost"}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
